import React, { useState, useEffect } from 'react';
import AdminDashboardSkeleton from "../../components/layout/AdminDashboardSkeleton";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';
import { Users, Settings, TrendingUp, UserCheck, Calendar, CheckCircle, XCircle, Loader, CreditCard, BarChart3, Activity } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// A chart's window: a preset, or a custom from/to range once both dates are set.
const DEFAULT_PERIOD = { timeframe: 'day', from: '', to: '' };

// Query params for a chart window, or null while a custom range is incomplete
// (the chart keeps its last data until both dates are in and in order).
const periodParams = (p) => {
    if (p.timeframe !== 'custom') return { timeframe: p.timeframe };
    return p.from && p.to && p.from <= p.to ? { timeframe: 'custom', from: p.from, to: p.to } : null;
};

// Timeframe select for a chart, with from/to date inputs when "Custom range" is
// chosen. Presets follow the calendar (Evan, Oct 9): Monthly is the 1st to the
// last day of this month, Yearly is Jan 1 to Dec 31; Daily and Weekly look back
// 30 days and 12 weeks. The backend picks the bucket size for a custom range.
function PeriodPicker({ value, onChange, isDarkMode }) {
    const selectCls = `select-styled text-sm font-medium rounded-xl px-3 py-2 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`;
    const dateCls = `text-sm rounded-xl px-2 py-2 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`;
    return (
        <div className="ml-auto flex flex-wrap items-center gap-2">
            {value.timeframe === 'custom' && (
                <>
                    <input type="date" className={dateCls} value={value.from} max={value.to || undefined} aria-label="From date"
                        onChange={e => onChange({ ...value, from: e.target.value })} />
                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>to</span>
                    <input type="date" className={dateCls} value={value.to} min={value.from || undefined} aria-label="To date"
                        onChange={e => onChange({ ...value, to: e.target.value })} />
                </>
            )}
            <select value={value.timeframe} onChange={e => onChange({ ...value, timeframe: e.target.value })} className={selectCls}>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
                <option value="custom">Custom range</option>
            </select>
        </div>
    );
}

// Runs, completed / failed counts and success rates per period from
// /admin/analysis-history, for one card's own period picker.
function useAnalysisHistory(period) {
    const [history, setHistory] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const params = periodParams(period);
        if (!params) return;
        let cancelled = false;
        setLoading(true);
        api.get('/admin/analysis-history', { params })
            .then(res => {
                if (cancelled) return;
                setHistory(res.data.history || []);
                setTotals(res.data.totals || {});
            })
            .catch(err => {
                console.error('Error fetching analysis history:', err);
                if (cancelled) return;
                setHistory([]);
                setTotals({});
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [period]);
    return { history, totals, loading };
}


export default function AdminDashboard() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    const [cardTimeframe, setCardTimeframe] = useState('day');

    const [memberStats, setMemberStats] = useState({ count: 0, new: 0, percent: 0 });
    const [userStats, setUserStats] = useState({ count: 0, new: 0, percent: 0 });
    const [taskStats, setTaskStats] = useState({ count: 0, new: 0, percent: 0 });
    const [activeUsers, setActiveUsers] = useState({ current: 0, recent: 0 });

    const [growthHistory, setGrowthHistory] = useState([]);
    const [growthLoading, setGrowthLoading] = useState(true);
    const [growthPeriod, setGrowthPeriod] = useState(DEFAULT_PERIOD);

    const [memberGrowthHistory, setMemberGrowthHistory] = useState([]);
    const [memberGrowthLoading, setMemberGrowthLoading] = useState(true);
    const [memberPeriod, setMemberPeriod] = useState(DEFAULT_PERIOD);
    const [memberTierNames, setMemberTierNames] = useState([]);
    const [activeTier, setActiveTier] = useState(null);

    // AI analysis runs and success rates. Each card has its own period picker
    // and fetch, like the two growth charts above them.
    const [runsPeriod, setRunsPeriod] = useState(DEFAULT_PERIOD);
    const [ratePeriod, setRatePeriod] = useState(DEFAULT_PERIOD);
    const runs = useAnalysisHistory(runsPeriod);
    const rates = useAnalysisHistory(ratePeriod);

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    useEffect(() => {
        const fetchTotalUsers = async () => {
            setLoading(true);
            try {
                const res = await api.get('/admin/users', { params: { active_only: false, page: 1, per_page: 1 } });
                setUserStats(prev => ({ ...prev, count: res.data.total || 0 }));
            } catch (err) {
            } finally {
                setLoading(false);
            }
        };
        fetchTotalUsers();
    }, []);

    useEffect(() => {
        // "New today / this week / this month" comes from a real count of signups
        // inside the current period (Houston time). The growth-history buckets
        // used before only exist for days that had a signup, so the "latest
        // bucket" stayed stuck on the last busy day and quiet periods showed 0.
        const fetchUserStats = async () => {
            try {
                const res = await api.get('/admin/signup-stats', { params: { timeframe: cardTimeframe } });
                const current = res.data.current || 0;
                const previous = res.data.previous || 0;
                const percent = previous > 0 ? (((current - previous) / previous) * 100).toFixed(1) : (current > 0 ? 100 : 0);
                setUserStats(prev => ({ ...prev, new: current, percent }));
            } catch (err) {
                setUserStats(prev => ({ ...prev, new: 0, percent: 0 }));
            }
        };
        fetchUserStats();
    }, [cardTimeframe]);

    useEffect(() => {
        // Fetch task stats for card (total + growth)
        const fetchTaskStats = async () => {
            try {
                const res = await api.get('/admin/task-growth-history', { params: { timeframe: cardTimeframe } });
                const history = res.data.history || [];
                const total = res.data.total || 0;
                const count = history.length > 0 ? history[history.length - 1].task_count : 0;
                const prev = history.length > 1 ? history[history.length - 2].task_count : 0;
                const newCount = count - prev;
                const percent = prev > 0 ? ((newCount / prev) * 100).toFixed(1) : (count > 0 ? 100 : 0);
                setTaskStats({
                    count: total,
                    new: newCount > 0 ? newCount : 0,
                    percent
                });
            } catch (err) {
                setTaskStats(prev => ({ ...prev, new: 0, percent: 0 }));
            }
        };
        fetchTaskStats();
    }, [cardTimeframe]);

    useEffect(() => {
        const fetchActiveUsers = async () => {
            try {
                const res = await api.get('/admin/active-users', { params: { timeframe: cardTimeframe } });
                setActiveUsers(res.data || { current: 0, recent: 0 });
            } catch (err) {
                setActiveUsers({ current: 0, recent: 0 });
            }
        };
        fetchActiveUsers();
    }, [cardTimeframe]);

    useEffect(() => {
        const params = periodParams(growthPeriod);
        if (!params) return;
        const fetchGrowthHistory = async () => {
            setGrowthLoading(true);
            try {
                const response = await api.get('/admin/user-growth-history', { params });
                setGrowthHistory(response.data.history || []);
            } catch (err) {
                console.error('Error fetching user growth history:', err);
                setGrowthHistory([]);
            } finally {
                setGrowthLoading(false);
            }
        };
        fetchGrowthHistory();
    }, [growthPeriod]);

    useEffect(() => {
        const params = periodParams(memberPeriod);
        if (!params) return;
        const fetchMemberGrowthHistory = async () => {
            setMemberGrowthLoading(true);
            try {
                const response = await api.get('/admin/member-growth-history', { params });
                setMemberGrowthHistory(response.data.history || []);
                setMemberTierNames(response.data.tiers || []);
            } catch (err) {
                console.error('Error fetching member growth history:', err);
                setMemberGrowthHistory([]);
                setMemberTierNames([]);
            } finally {
                setMemberGrowthLoading(false);
            }
        };
        fetchMemberGrowthHistory();
    }, [memberPeriod]);

    if (loading) {
        return <AdminDashboardSkeleton isDarkMode={isDarkMode} />;
    }

    const getTimeframeLabel = (tf) => {
        switch (tf) {
            case 'day': return 'today';
            case 'week': return 'this week';
            case 'month': return 'this month';
            default: return '';
        }
    };

    // Chart labels come from the backend as ready-to-show period names ("Oct 6",
    // "Week of Oct 6", "Oct 2026"), one row per period in the window, so the
    // axis never parses a date here. (Parsing week numbers and bare years with
    // new Date() is what once produced "December 31, 1969" ticks.)
    const chartText = isDarkMode ? '#94a3b8' : '#64748b';
    const tooltipStyle = isDarkMode ? { backgroundColor: '#1e293b', border: '1px solid #334155', color: '#f1f5f9' } : {};

    // "Resume 98% (49 of 50)" for the window, or a plain note when nothing finished.
    const rateSummary = (t, key, label) => {
        const finished = (t[`${key}_ok`] || 0) + (t[`${key}_failed`] || 0);
        return finished ? `${label} ${t[`${key}_rate`]}% (${t[`${key}_ok`]} of ${finished})` : `${label}: no finished runs`;
    };
    // Tooltip line for one rate point: "95% (19 of 20)".
    const rateTooltip = (value, name, item) => {
        const key = String(item.dataKey).replace('_rate', '');
        const row = item.payload || {};
        const finished = (row[`${key}_ok`] || 0) + (row[`${key}_failed`] || 0);
        return [`${value}% (${row[`${key}_ok`] || 0} of ${finished})`, name];
    };

    const card = `rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const selectCls = `px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`;

    return (
        <>
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Admin Dashboard</h1>
                            <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage users, lookups, and view analytics</p>
                        </div>
                        <button
                            onClick={() => navigate('/admin/system-logs')}
                            className={`px-4 py-2 rounded-lg font-semibold shadow hover:bg-red-200 transition-colors flex items-center gap-2 ${isDarkMode ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-100 text-red-700'}`}
                            aria-label="View System Logs"
                        >
                            <XCircle size={20} />
                            System Logs
                        </button>
                    </div>
                </header>

                {/* Quick Stats */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
                    <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Quick Stats</h2>
                    <div>
                        <select
                            value={cardTimeframe}
                            onChange={e => setCardTimeframe(e.target.value)}
                            className={`select-styled text-sm font-medium rounded-xl px-3 py-2 border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                        >
                            <option value="day">Daily</option>
                            <option value="week">Weekly</option>
                            <option value="month">Monthly</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Total Members Card (now shows total users and new users) */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                                <UserCheck size={24} />
                            </div>
                        </div>
                        <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total Members</h3>
                        <div className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{userStats.count}</div>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-primary-600 font-semibold">+{userStats.new} new {getTimeframeLabel(cardTimeframe)}</span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>({userStats.percent}% vs previous {cardTimeframe})</span>
                        </div>
                    </div>
                    {/* Total Tasks Card */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
                                <Calendar size={24} />
                            </div>
                        </div>
                        <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total Tasks</h3>
                        <div className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{taskStats.count}</div>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-amber-600 font-semibold">+{taskStats.new} new {getTimeframeLabel(cardTimeframe)}</span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>({taskStats.percent}% new)</span>
                        </div>
                    </div>
                    {/* Active Users Card */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                <Users size={24} />
                            </div>
                        </div>
                        <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Active Users</h3>
                        <div className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{activeUsers.current}</div>
                        <div className="text-xs mt-2 text-slate-500">Recent (last 48h): {activeUsers.recent}</div>
                    </div>
                </div>

                {/* Growth Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* User Growth Chart Section */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center gap-4 mb-4">
                            <TrendingUp size={24} className="text-primary-600" />
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>User Growth Rate</h2>
                            <PeriodPicker value={growthPeriod} onChange={setGrowthPeriod} isDarkMode={isDarkMode} />
                        </div>
                        {growthLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader className="animate-spin text-primary-600" size={32} />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={growthHistory} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                                    <XAxis dataKey="period" tick={{ fontSize: 12, fill: chartText }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: chartText }} />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Legend />
                                    <Line type="monotone" dataKey="user_count" stroke="#5A7CDE" name="Users" />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Paid Membership Growth Chart Section */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center gap-4 mb-4">
                            <TrendingUp size={24} className="text-purple-600" />
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Paid Membership Growth Rate</h2>
                            <PeriodPicker value={memberPeriod} onChange={setMemberPeriod} isDarkMode={isDarkMode} />
                        </div>
                        {memberGrowthLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader className="animate-spin text-purple-600" size={32} />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={memberGrowthHistory} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <Tooltip />
                                    <Legend
                                        onClick={(e) => setActiveTier(prev => prev === e.dataKey ? null : e.dataKey)}
                                        wrapperStyle={{ cursor: 'pointer' }}
                                    />
                                    {memberTierNames.map((tier, idx) => {
                                        const colors = ['#a855f7', '#ec4899', '#f59e0b', '#06b6d4', '#84cc16', '#ef4444'];
                                        const isHidden = activeTier && activeTier !== tier;
                                        return (
                                            <Line
                                                key={tier}
                                                type="monotone"
                                                dataKey={tier}
                                                stroke={colors[idx % colors.length]}
                                                name={tier}
                                                hide={isHidden}
                                                strokeOpacity={isHidden ? 0 : 1}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* AI Analysis: runs per period and how many of them finished (Evan,
                    Oct 9). Each card has its own period picker and fetch, like the
                    growth charts. The backend returns every period zero-filled, with
                    the failures each feature already records as failed / error; a
                    period with no finished runs has no rate point. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Analyses Run */}
                    <div className={card}>
                        <div className="flex items-center gap-4 mb-1">
                            <BarChart3 size={24} className="text-emerald-600" />
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Analyses Run</h2>
                            <PeriodPicker value={runsPeriod} onChange={setRunsPeriod} isDarkMode={isDarkMode} />
                        </div>
                        <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {`${runs.totals.resume_runs || 0} resume, ${runs.totals.pitch_runs || 0} elevator pitch, ${runs.totals.interview_runs || 0} mock interview in this window`}
                        </p>
                        {runs.loading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader className="animate-spin text-emerald-600" size={32} />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={runs.history} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                                    <XAxis dataKey="period" tick={{ fontSize: 12, fill: chartText }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: chartText }} />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Legend />
                                    <Bar dataKey="resume_runs" name="Resume" fill="#5A7CDE" />
                                    <Bar dataKey="pitch_runs" name="Elevator Pitch" fill="#a855f7" />
                                    <Bar dataKey="interview_runs" name="Mock Interview" fill="#f59e0b" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Analysis Success Rate */}
                    <div className={card}>
                        <div className="flex items-center gap-4 mb-1">
                            <Activity size={24} className="text-rose-600" />
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Analysis Success Rate</h2>
                            <PeriodPicker value={ratePeriod} onChange={setRatePeriod} isDarkMode={isDarkMode} />
                        </div>
                        <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {[rateSummary(rates.totals, 'resume', 'Resume'), rateSummary(rates.totals, 'pitch', 'Pitch'), rateSummary(rates.totals, 'interview', 'Interview')].join(', ')}
                        </p>
                        {rates.loading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader className="animate-spin text-rose-600" size={32} />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={rates.history} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                                    <XAxis dataKey="period" tick={{ fontSize: 12, fill: chartText }} />
                                    <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12, fill: chartText }} />
                                    <Tooltip contentStyle={tooltipStyle} formatter={rateTooltip} />
                                    <Legend />
                                    <Line type="monotone" dataKey="resume_rate" stroke="#5A7CDE" name="Resume" />
                                    <Line type="monotone" dataKey="pitch_rate" stroke="#a855f7" name="Elevator Pitch" />
                                    <Line type="monotone" dataKey="interview_rate" stroke="#f59e0b" name="Mock Interview" />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <button
                        onClick={() => navigate('/admin/users')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                <Users size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Manage Users</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>View and manage user accounts</p>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => navigate('/admin/employees')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600'}`}>
                                <UserCheck size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Employee Management</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage employees and roles</p>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => navigate('/admin/lookups')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                                <Settings size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Manage Lookups</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Edit personas, intents, industries, positions</p>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => navigate('/admin/memberships')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                                <CreditCard size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Membership Management</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage tiers and view active members</p>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
}
